'use client'

import { useState } from 'react'
import { Link, Modal, Typography } from '@shc/ui'
import LocationHours from '@app/[locale]/(main)/locations/_components/location-hours'
import { type SearchNamespace } from '@app/[locale]/(main)/search/_components/search-session'
import type {
  TypePlacePublic,
  TypeScheduleAmended,
  TypeScheduleException,
} from '@lib/location-types'
import { AnalyticsLink } from '@components/analytics'
import useAnalytics from '@hooks/use-analytics'

type LocationDetailProps = {
  placeId: TypePlacePublic['id']
  now: string
  name: string
  description?: string | null
  address?: string | null
  phone?: string | null
  fax?: string | null
  mapUrl?: string
  displayHours?: boolean
  schedule?: TypeScheduleAmended[]
  scheduleNotes?: string | null
  scheduleExceptions?: TypeScheduleException[]
  link?: string | null
}

const LocationDetail = ({
  placeId,
  now,
  name,
  description,
  address,
  phone,
  fax,
  mapUrl,
  displayHours = false,
  schedule,
  scheduleNotes,
  scheduleExceptions,
  link,
}: LocationDetailProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { trackSnowplow } = useAnalytics()

  // Analytics tracking vars
  const objectId = placeId?.toString() ?? '-1'
  const namespace: SearchNamespace = 'places'

  const handleOnClose = () => {
    setIsModalOpen(false)
    trackSnowplow({
      event: { name: 'modal_close', data: {} },
      contexts: [{ name: 'modal', data: { modal_name: 'DepartmentHoursModal' } }],
    })
  }

  return (
    <div>
      <Typography variant="h2" className="pb-6">
        How to find us
      </Typography>
      <div className="flex flex-row gap-5 items-center">
        <div className="flex flex-col gap-3 md:basis-1/2">
          {link ? (
            <Link noUnderline href={link}>
              {name}
            </Link>
          ) : (
            <Typography variant="body-semibold">{name}</Typography>
          )}
          {description && <Typography>{description}</Typography>}
          {address && (
            <div>
              <Typography className="whitespace-pre-line">{address}</Typography>
              <AnalyticsLink
                href={`https://google.com/maps/search/?api=1&query=${name}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get directions"
                noUnderline
                algolia={{ conversion: { namespace, objectId } }}>
                Get directions
              </AnalyticsLink>
            </div>
          )}
          {phone && (
            <div>
              <AnalyticsLink
                noUnderline
                href={`tel:+1${phone}`}
                algolia={{ conversion: { namespace, objectId } }}>
                {phone}
              </AnalyticsLink>
              {fax && <Typography>Fax: {fax}</Typography>}
            </div>
          )}
          {displayHours && (
            <div>
              <LocationHours
                now={now}
                schedule={schedule as TypeScheduleAmended[]}
                scheduleNotes={scheduleNotes}
                scheduleExceptions={
                  Array.isArray(scheduleExceptions) ? scheduleExceptions : undefined
                }
                summaryOnly={true}
              />
              <Link as="button" noUnderline onClick={() => setIsModalOpen(true)}>
                View hours
              </Link>
            </div>
          )}
        </div>
        {mapUrl && (
          <div className="hidden md:block md:basis-1/2">
            <AnalyticsLink
              href={`https://google.com/maps/search/?api=1&query=${name}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get directions"
              algolia={{ conversion: { namespace, objectId } }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={mapUrl}
                alt="Google Map"
                aria-label="Get directions"
                className="rounded w-full object-contain object-top"
              />
            </AnalyticsLink>
          </div>
        )}
      </div>
      {displayHours && (
        <Modal width="sm" open={isModalOpen} onClose={handleOnClose}>
          <LocationHours
            now={now}
            schedule={schedule}
            scheduleNotes={scheduleNotes}
            scheduleExceptions={Array.isArray(scheduleExceptions) ? scheduleExceptions : undefined}
          />
        </Modal>
      )}
    </div>
  )
}

export default LocationDetail
